import { useNavigate } from "#routers/hooks";
import { UsersDropdownInput } from "#src/batteries-included-components/Dropdowns/UsersDropdownInput";
import { useCreateOneWorker } from "#src/components/hooks/adapters/useWorkers";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import { ExceptionUtils } from "#src/utils/exception";
import {
  Button,
  Dialog,
  Form,
  TextAreaInput,
  TextInput,
  toast,
  useForm,
} from "@validereinc/common-components";
import {
  CreateWorkerFormSchema,
  MAX_WORKER_NAME_LENGTH,
  UserTypeEnum,
  WORKER_NAME_REGEX,
  type CreateWorkerFormType,
} from "@validereinc/domain";
import React from "react";

const CREATE_WORKER_DIALOG_TITLE = "Create Worker";

export const CreateWorkerDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const form = useForm<CreateWorkerFormType>({ mode: "onChange" });

  const createWorkerMutation = useCreateOneWorker({
    onSuccess: (response) => {
      toast.push({
        intent: "success",
        description: "Successfully created worker",
      });

      navigate(
        WorkerDetailRoutePath.toLinkParts({
          pathParams: { workerId: response.data.id },
        })
      );

      onCloseCallback();
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error", {
        hint: "Error creating worker",
      });
      toast.push({
        intent: "error",
        description: "Failed to create worker",
      });
    },
    noAlerts: true,
  });

  const onSubmit = form.handleSubmit((formValues) => {
    createWorkerMutation.mutate({ data: formValues });
  });

  const onCloseCallback = () => {
    form.reset();

    onClose();
  };

  const isFormValid = form.formState.isValid;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCloseCallback}
      title={CREATE_WORKER_DIALOG_TITLE}
      actionRow={[
        <Button
          key="create-worker-action"
          onClick={onSubmit}
          variant="primary"
          isLoading={createWorkerMutation.isLoading}
          disabled={!isFormValid}
        >
          {CREATE_WORKER_DIALOG_TITLE}
        </Button>,
      ]}
    >
      <Form {...form}>
        <TextInput
          label="Name"
          name={CreateWorkerFormSchema.keyof().Enum.name}
          placeholder="Enter Name..."
          isFluid
          isRequired
          rules={{
            validate: {
              maxLength: (name) =>
                name.length <= MAX_WORKER_NAME_LENGTH ||
                `Name must be less than or equal to ${MAX_WORKER_NAME_LENGTH} characters.`,
              regex: (name) =>
                WORKER_NAME_REGEX.test(name) ||
                "Name must be alphanumeric, hyphens, or underscores",
            },
          }}
        />
        <TextAreaInput
          label="Description"
          name={CreateWorkerFormSchema.keyof().Enum.description}
          placeholder="Enter Description..."
          isFluid
          rules={{
            validate: {
              maxLength: (description) => {
                if (!description) return true;

                return (
                  description.length <= 256 ||
                  "Description must be less than or equal to 256 characters."
                );
              },
            },
          }}
        />
        <UsersDropdownInput
          label="Machine User"
          name={CreateWorkerFormSchema.keyof().Enum.machine_user_id}
          placeholder="Select machine user..."
          description="Select the machine user to be associated to the worker."
          isRequired
          isInline
          isOptionalTextShown={false}
          typeFilter={[UserTypeEnum.machine]}
        />
      </Form>
    </Dialog>
  );
};
